/**
 * Para revisar (0156): lo que cargó otra persona a mano en Caja.
 *
 * En noviembre se cargaron como «Sueldo de socio» el cloro y el agua, y como
 * «Otro» un pago a un proveedor. El dueño eligió que lo arreglen los que lo
 * usan, explicando por qué: cada movimiento cargado a mano lo revisa OTRA
 * persona. Lo da por bueno («Está bien así») o le cambia la categoría, y en
 * los dos casos queda escrito quién, cuándo y por qué.
 *
 * Los dudosos van primero y marcados: un «Otro», o una categoría que no se
 * corresponde con lo que dice el detalle. Ahí la explicación es obligatoria.
 */
import { useState } from "react";
import { Check, Pencil, TriangleAlert } from "lucide-react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Segmented } from "@/components/ui/segmented";
import { Textarea } from "@/components/ui/textarea";
import { ChipsSocio } from "@/components/socios/ChipsSocio";
import { formatearFechaHora } from "@/lib/utils";
import { aNumero, type CuentaFinanciera, type MovimientoCaja, type Socio } from "@/lib/tipos";

import {
  ETIQUETA_CONCEPTO,
  OPERACIONES,
  buscarOperacion,
  esDeSocio,
  nombreCuenta,
  sugerirCategoria,
  type Operacion,
} from "./logica";

export interface Revision {
  id: number;
  concepto?: string;
  socio_id?: number;
  nota?: string;
}

export interface DialogRevisarProps {
  abierto: boolean;
  onCerrar: () => void;
  movimientos: ReadonlyArray<MovimientoCaja>;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  socios?: ReadonlyArray<Socio>;
  proveedores?: ReadonlyArray<{ nombre: string }>;
  guardando?: boolean;
  onRevisar: (r: Revision) => void;
}

/** La operacion con que se cargo: la de socio si tiene socio. */
function operacionDe(m: MovimientoCaja): Operacion | undefined {
  return (
    OPERACIONES.find(
      (o) => o.concepto === m.concepto && o.tipo === m.tipo && o.deSocio === (m.socio_id != null),
    ) ?? OPERACIONES.find((o) => o.concepto === m.concepto && o.tipo === m.tipo)
  );
}

/** Si conviene mirarlo con cuidado, y por que. */
export function dudaDe(
  m: MovimientoCaja,
  proveedores: ReadonlyArray<{ nombre: string }> = [],
): string | null {
  const op = operacionDe(m);
  const sugerencia = sugerirCategoria(m.detalle ?? "", op, proveedores);
  if (sugerencia) return sugerencia.texto;
  if (m.concepto === "OTRO") return "Está cargado como «Otro»: contá qué fue.";
  return null;
}

export function DialogRevisar({
  abierto,
  onCerrar,
  movimientos,
  cuentas,
  socios = [],
  proveedores = [],
  guardando = false,
  onRevisar,
}: DialogRevisarProps): JSX.Element {
  const [activo, setActivo] = useState<{ id: number; modo: "bien" | "cambiar" } | null>(null);
  const [clave, setClave] = useState("");
  const [socioId, setSocioId] = useState("");
  const [nota, setNota] = useState("");

  const ordenados = [...movimientos].sort(
    (a, b) => Number(dudaDe(b, proveedores) !== null) - Number(dudaDe(a, proveedores) !== null),
  );

  function abrir(m: MovimientoCaja, modo: "bien" | "cambiar"): void {
    setActivo({ id: m.id, modo });
    setClave(modo === "cambiar" ? "" : (operacionDe(m)?.clave ?? ""));
    setSocioId(m.socio_id != null ? String(m.socio_id) : "");
    setNota("");
  }

  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent tamano="md" mobile="hoja">
        <DialogHeader>
          <DialogTitle>Para revisar</DialogTitle>
          <DialogDescription>
            Lo cargó otra persona. Confirmá que esté bien o cambiale la categoría, y contá por qué:
            queda escrito en el movimiento.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {ordenados.length === 0 ? (
            <p className="text-sm text-muted-foreground">No queda nada para revisar.</p>
          ) : (
            <ul className="space-y-3">
              {ordenados.map((m) => {
                const op = operacionDe(m);
                const duda = dudaDe(m, proveedores);
                const esActivo = activo?.id === m.id;
                const opNueva = buscarOperacion(clave);
                const pideSocio = activo?.modo === "cambiar" && esDeSocio(opNueva);
                const notaObligatoria = activo?.modo === "cambiar" || duda !== null;
                const listo =
                  nota.trim().length >= (notaObligatoria ? 3 : 0) &&
                  (activo?.modo !== "cambiar" ||
                    (opNueva !== undefined && (!pideSocio || socioId)));
                return (
                  <li key={m.id}>
                    <Card padding="compacto" className="space-y-2" acento={m.moneda}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-0.5">
                          <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                            <Badge variant={duda ? "warning" : "secondary"}>
                              {op?.label ?? ETIQUETA_CONCEPTO[m.concepto] ?? m.concepto}
                            </Badge>
                            <span className="truncate">{m.detalle ?? "Sin detalle"}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatearFechaHora(m.fecha)} · {nombreCuenta(cuentas, m.cuenta_id)} ·
                            Lo cargó {m.usuario_nombre ?? "alguien"}
                          </p>
                        </div>
                        <Importe
                          monto={m.tipo === "EGRESO" ? -aNumero(m.importe) : aNumero(m.importe)}
                          moneda={m.moneda}
                          tamano="sm"
                          signoExplicito
                        />
                      </div>

                      {duda ? (
                        <p className="flex items-start gap-1 text-xs text-warning">
                          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                          {duda}
                        </p>
                      ) : null}

                      {esActivo ? (
                        <div className="space-y-3 border-t border-border pt-3">
                          {activo.modo === "cambiar" ? (
                            <div className="space-y-1.5">
                              <Label>La categoría que corresponde</Label>
                              <Segmented
                                apariencia="chips"
                                tamano="sm"
                                etiqueta="Categoría correcta"
                                opciones={OPERACIONES.filter(
                                  (o) => o.tipo === m.tipo && o.clave !== op?.clave,
                                ).map((o) => ({ valor: o.clave, etiqueta: o.label }))}
                                valor={clave}
                                onChange={setClave}
                              />
                              {opNueva ? (
                                <p className="text-xs text-muted-foreground">{opNueva.ayuda}</p>
                              ) : null}
                              {pideSocio ? (
                                <ChipsSocio socios={socios} valor={socioId} onChange={setSocioId} />
                              ) : null}
                            </div>
                          ) : null}
                          <div className="space-y-1.5">
                            <Label htmlFor={`nota-${m.id}`}>
                              {activo.modo === "cambiar"
                                ? "¿Por qué se había cargado así?"
                                : notaObligatoria
                                  ? "¿Qué fue?"
                                  : "Comentario (opcional)"}
                            </Label>
                            <Textarea
                              id={`nota-${m.id}`}
                              rows={2}
                              value={nota}
                              onChange={(e) => setNota(e.target.value)}
                              placeholder={
                                activo.modo === "cambiar"
                                  ? "Ej: no sabía dónde iba el agua"
                                  : "Ej: GFM es el que arregla las vitrinas"
                              }
                            />
                          </div>
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setActivo(null)}
                            >
                              Volver
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              cargando={guardando}
                              disabled={!listo}
                              onClick={() =>
                                onRevisar({
                                  id: m.id,
                                  ...(activo.modo === "cambiar" && opNueva
                                    ? {
                                        concepto: opNueva.concepto,
                                        ...(pideSocio && socioId
                                          ? { socio_id: Number(socioId) }
                                          : {}),
                                      }
                                    : {}),
                                  ...(nota.trim() ? { nota: nota.trim() } : {}),
                                })
                              }
                            >
                              {activo.modo === "cambiar" ? "Cambiar categoría" : "Confirmar"}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => abrir(m, "cambiar")}
                          >
                            <Pencil aria-hidden="true" />
                            Cambiar categoría
                          </Button>
                          <Button type="button" size="sm" onClick={() => abrir(m, "bien")}>
                            <Check aria-hidden="true" />
                            Está bien así
                          </Button>
                        </div>
                      )}
                    </Card>
                  </li>
                );
              })}
            </ul>
          )}
          <Aviso tono="neutral" compacto>
            Si era un pago a un proveedor, anulalo desde la lista de Caja y cargalo desde
            Proveedores: así baja lo que se le debe y no cuenta como gasto.
          </Aviso>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
